import { ActionRuleParams } from '../../types';
import { grillJacketSkuMap } from './grill-jacket-sku-map.data';
import { Island } from '../../../islands/island.class';
import { PanelItem } from '../../../panel-items/panel-item.class';

const jacketSkus = new Set(Object.values(grillJacketSkuMap));

// Fuel appliances are one catalog row per fuel (see apps/api/prisma/split-fuel-variants.js), so
// `item.sku` on a placed grill is the variant sku (`C3SL36NG`) while the jacket map is keyed by
// the family (`C3SL36`). Fall back to sku for un-split rows and non-fuel items (the jackets).
const grillMapKey = (panelItem: PanelItem): string => panelItem.item.component?.fuelFamily ?? panelItem.item.sku ?? '';

const applyInsulatedJacketForIsland = (island: Island, params: ActionRuleParams): void => {
	const { refMap } = params;

	const surroundKey = island.surround?.key ?? '';
	const isMok = surroundKey.startsWith('moks/');

	island.panels.children.forEach((panel) => {
		panel.panelItemGroups.children.forEach((group) => {
			const grills = group.items.children.filter((panelItem) => grillJacketSkuMap[grillMapKey(panelItem)]);
			const ruleManagedJackets = group.items.children.filter((panelItem) => {
				return jacketSkus.has(panelItem.item.sku) && panelItem.ruleManaged;
			});

			if (isMok) {
				// skip groups where an item is already in a sleeve or jacket
				const hasSleeve = group.items.children.some((panelItem) => panelItem.item.component.sleeveDepth !== null);
				if (hasSleeve) {
					// sleeve present -- remove any stale rule-managed jackets left from before the sleeve was added
					ruleManagedJackets.forEach((jacket) => {
						group.items.delete(jacket.id);
					});
					return;
				}

				// for each grill that has a mapped jacket, ensure the jacket exists in the same group
				grills.forEach((grill) => {
					const jacketSku = grillJacketSkuMap[grillMapKey(grill)];
					const hasJacket = group.items.children.some((panelItem) => panelItem.item.sku === jacketSku && panelItem.ruleManaged);

					if (!hasJacket) {
						// catalog drift guard: never place a rule-managed jacket the catalog can no longer resolve
						if (!island.context.components.findSku(jacketSku)) {
							island.context.logger.warn(`requiresInsulatedJacket: jacket sku "${jacketSku}" not found in catalog -- skipping for grill "${grillMapKey(grill)}"`);
							return;
						}

						const jacket = group.items.newFromSku(jacketSku);
						jacket.ruleManaged = true;
					}
				});
			} else {
				// not MOK -- remove all rule-managed jackets from this group
				ruleManagedJackets.forEach((jacket) => {
					group.items.delete(jacket.id);
				});
			}
		});
	});
};

export const requiresInsulatedJacket = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { island } = refMap;

	// single-island scope -- apply directly
	if (island) {
		applyInsulatedJacketForIsland(island, params);
		return;
	}

	// kitchen scope -- iterate all islands
	const islands = refMap.islands ?? refMap.kitchen?.design?.islands ?? null;
	if (!islands) return;

	for (const child of islands.children) {
		applyInsulatedJacketForIsland(child as Island, params);
	}
};
